import PropTypes from "prop-types";
import { useState } from "react";
import { useShopFilters } from "../hooks/index.js";
import Expandable from "./Expandable.jsx";
import {
  CheckIcon,
  ChevronDownIcon,
  FilterIcon,
  SearchIcon,
} from "./Icons.jsx";
import SearchCombobox from "./SearchCombobox.jsx";
import TalentPanel from "./TalentPanel.jsx";
import tp from "./TalentPanel.module.css";
import s from "./Toolbar.module.css";
import { Skeleton, Typography } from "../../../themeV2";

export default function Toolbar({ isCollection, isLoading }) {
  const {
    activeFormat,
    activeSigningType,
    activeVa,
    sortBy,
    searchQuery,
    totalVisible,
    setActiveFormat,
    setActiveSigningType,
    setActiveCharacter,
    setActiveVa,
    setSortBy,
    setSearchQuery,
    hasActiveFilters,
    handleClearAll,
    filterLabels,
    characters,
    formatOptions,
    signingTypes,
    vaList,
  } = useShopFilters();

  const [filtersOpen, setFiltersOpen] = useState(false);
  const [talentOpen, setTalentOpen] = useState(false);

  const showTalent = isCollection && vaList?.length > 1;

  if (isLoading) {
    return (
      <nav className={s.toolbar} role="search" aria-label="Product filters">
        <div className={s.row}>
          <div className={`${s.searchField} ${s.searchFieldDisabled}`}>
            <SearchIcon />
            <span className={s.searchPlaceholder}>
              Search products, characters...
            </span>
          </div>
        </div>
        <div className={s.row}>
          <Skeleton variant="text" as="span" style={{ width: 80 }} />
          <Skeleton variant="text" as="span" style={{ width: 120 }} />
          <Skeleton variant="text" as="span" style={{ width: 64 }} />
          <Skeleton variant="text" as="span" style={{ width: 100 }} />
        </div>
      </nav>
    );
  }

  return (
    <nav className={s.toolbar} role="search" aria-label="Product filters">
      <div className={s.row}>
        <SearchCombobox
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          characters={characters}
          vaList={vaList}
          setActiveCharacter={setActiveCharacter}
          setActiveVa={setActiveVa}
          handleClearAll={handleClearAll}
          filterLabels={filterLabels}
        />
        <div
          className={s.filterToggleGroup}
          role="group"
          aria-label="Filter controls"
        >
          <button
            className={`${s.filterToggle}${
              hasActiveFilters ? ` ${s.filterToggleActive}` : ""
            }`}
            onClick={() => setFiltersOpen((o) => !o)}
            aria-expanded={filtersOpen}
            aria-controls="toolbar-filters"
          >
            <FilterIcon />
            <span>Filters</span>
            {hasActiveFilters && (
              <span
                className={s.filterToggleBadge}
                aria-label="Active filters applied"
              />
            )}
          </button>
          {hasActiveFilters && (
            <button
              className={s.filterClearBtn}
              onClick={handleClearAll}
              aria-label="Clear all filters"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      <div
        id="toolbar-filters"
        className={`${s.filters}${filtersOpen ? ` ${s.filtersOpen}` : ""}`}
      >
        <div className={s.filtersInner}>
          <div className={s.filterCluster}>
            <Typography
              variant="label-lg"
              color="variant"
              weight="bold"
              as="span"
              className={s.filterGroupLabel}
            >
              Format
            </Typography>
            <div className={s.filterGroup}>
              {formatOptions.map((f) => (
                <button
                  key={f.category}
                  className={s.chip}
                  aria-pressed={activeFormat === f.category}
                  onClick={() => setActiveFormat(f.category)}
                >
                  {activeFormat === f.category && (
                    <CheckIcon className={s.checkIcon} />
                  )}
                  {f.label}
                </button>
              ))}
            </div>
          </div>

          <div className={s.filterCluster}>
            <Typography
              variant="label-lg"
              color="variant"
              weight="bold"
              as="span"
              className={s.filterGroupLabel}
            >
              Signing Type
            </Typography>
            <div className={s.filterGroup}>
              {signingTypes.map((st) => (
                <button
                  key={st.category}
                  className={s.chip}
                  aria-pressed={activeSigningType === st.category}
                  onClick={() => setActiveSigningType(st.category)}
                >
                  {activeSigningType === st.category && (
                    <CheckIcon className={s.checkIcon} />
                  )}
                  {st.label}
                </button>
              ))}
            </div>
          </div>

          <p className={s.resultCount} aria-live="polite">
            {totalVisible > 0
              ? `Show ${totalVisible} result${totalVisible === 1 ? "" : "s"}`
              : "No results found"}
          </p>
        </div>
      </div>

      <div className={s.extRow}>
        {showTalent && (
          <button
            className={tp.expandTrigger}
            aria-expanded={talentOpen}
            onClick={() => setTalentOpen(!talentOpen)}
          >
            <ChevronDownIcon className={tp.arrow} />
            Browse by Talent ({vaList.length})
          </button>
        )}

        <div className={`${s.filterCluster} ${s.sortFilter}`}>
          <Typography
            variant="label-lg"
            color="variant"
            weight="bold"
            as="label"
            htmlFor="toolbar-sort"
            className={s.filterGroupLabel}
          >
            Sort
          </Typography>
          <select
            id="toolbar-sort"
            className={s.sortSelect}
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
          >
            <option value="featured">Popular</option>
            <option value="newest">Newest</option>
            <option value="price-asc">Price: Low → High</option>
            <option value="price-desc">Price: High → Low</option>
            <option value="name-asc">Name A–Z</option>
            <option value="name-desc">Name Z–A</option>
          </select>
        </div>
      </div>

      {showTalent && (
        <Expandable open={talentOpen}>
          <TalentPanel
            vaList={vaList}
            activeVa={activeVa}
            onVaSelect={setActiveVa}
          />
        </Expandable>
      )}
    </nav>
  );
}

Toolbar.propTypes = {
  isCollection: PropTypes.bool,
  isLoading: PropTypes.bool,
};
